import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { useMemo, useRef, useId, useState, useLayoutEffect, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Icon16Dot } from '@figma/fpl-icons/icon-16-dot';
import { Icon24ChevronRight } from '@figma/fpl-icons/icon-24-chevron-right';
import { useColorTheme } from '@figma/fpl-themes';
import { useCancellableCallback } from '../hooks/use-cancellable-callback.js';
import { useInteractionHandler } from '../hooks/use-interaction-handler.js';
import { useMergeRefs } from '../hooks/use-merge-refs.js';
import { useSafeTriangle, SafeTriangle } from '../hooks/use-safe-triangle.js';
import { useTabLoop } from '../hooks/use-tab-loop.js';
import { useValidatedContext } from '../hooks/use-validated-context.js';
import { Surface } from '../surface/surface.js';
import { accept } from '../utils/event.js';
import { LastInteraction, getMovementType } from '../utils/interaction.js';
import { isAnyModifierPressed } from '../utils/modifiers.js';
import { MenuInternalContext, useSubMenuContainer, useMenuTargetRef, useMenuPositioner, useMenuNav, useMenuTypeahead } from './menu-v2.hooks.js';
import { ItemDisplay } from './menu-v2.item.js';
import { Popup } from './menu-v2.popup.js';
import { item, submenuTriggerTrail } from './menu-v2.css.js';

/** The time, in milliseconds, between hovering over a submenu trigger and displaying the submenu */
const SUBMENU_OPENED_DELAY_MS = 200;
/**
 * This number must be > 0, and far less than < 100 so as to not be perceptible.
 * This only prevents flickering when moving from trigger > SafePolygon.
 *
 * Note: this is NOT a setTimeout hack! This is an event we just want to queue
 * in callstack 1, optionally cancel in callstack 2, and only fire in callstack
 * 3 if not cancelled.
 *
 * @example
 * Scenario 1: user moves from trigger > SafePolygon
 * 1. <li onPointerLeave>           -> onMoveOutside queues
 * 2. <SafePolygon onPointerEnter>  -> onMoveOutside cancelled
 * 3. onMoveOutside (cancelled)
 *
 * In Scenario 1, we fire an event, and then cancel it immediately either in the
 * same callstack or the immediate one, so there’s no flicker.
 *
 * Scenario 2: user moves from trigger > (any other element)
 * 1. <li onPointerLeave>             -> onMoveOutside queues
 * 2. (no cancellation, other events) -> …
 * 3. onMoveOutside fires
 *
 * In Scenario 2, because the event isn’t immediately cancelled in the next
 * callstack, we can go ahead and fire it. Further, if, for some reason, the
 * cancel() call is delayed behind the event firing, and the event fires, that’s
 * OK. For all we can tell that was still a legitimate event.
 */
const SUBMENU_TIMEOUT_DELAY = 10;
// Important: if this is no longer static, then use useMemo() so it has a stable
// reference and a positioner is not recreated every render
const POSITION_ARGS = {
    position: {
        area: 'right span-bottom',
        margin: { x: 12, y: -8 },
    },
};
/**
 * Menu.SubMenu
 *
 * Submenu triggers are always enabled. Disable unavailable child items individually.
 *
 * @compose { "parent": ["MenuV2.Root", "MenuV2.SubMenu"], "multi": true, "strictChildren": true }
 */
function SubMenu({ children, colorTheme, hasChecked, id, recordingKey, targetId, title, titleLead, titleTrail, DEBUG_SAFE_TRIANGLE, }) {
    const { interactionMode, registerOpenSubMenu: registerSelfWithParent, onCloseRoot, isClosing, portalTarget, isClickHold, programmaticOpen, } = useValidatedContext(MenuInternalContext, 'MenuV2.SubMenu', 'MenuV2.Root');
    const { registerMenuTarget, programmaticTargetId, programmaticTargetPath, endProgrammaticOpen, releaseProgrammaticPointerItem, shouldIgnoreProgrammaticPointerEvent, } = programmaticOpen;
    const { registerOpenSubMenu } = useSubMenuContainer();
    const menuInternalContext = useMemo(() => ({
        interactionMode,
        registerOpenSubMenu,
        onCloseRoot,
        isClosing,
        portalTarget,
        isClickHold,
        programmaticOpen,
    }), [
        interactionMode,
        isClickHold,
        isClosing,
        onCloseRoot,
        portalTarget,
        programmaticOpen,
        registerOpenSubMenu,
    ]);
    // Submenus portal to document.body, so they fall outside the parent menu's
    // ColorTheme div in the DOM and can't inherit its `data-preferred-theme` via
    // CSS. React context still crosses the portal though, so we read the resolved
    // theme here and re-apply every dimension (mode, brand, contrast) on the
    // submenu's Popup. This makes the submenu follow the root menu's theme (dark
    // by default, light/branded when the root opts in) instead of forcing dark.
    // An explicit `colorTheme` prop wins.
    const inheritedTheme = useColorTheme();
    const popupRef = useRef(null);
    const anchorRef = useRef(null);
    const triggerRef = useRef(null);
    const programmaticTargetRef = useMenuTargetRef(targetId, registerMenuTarget);
    const ref = useMergeRefs(triggerRef, programmaticTargetRef);
    const popupId = `${id ? `${id}-` : ''}${useId()}`;
    const isProgrammaticOpen = Boolean(targetId && programmaticTargetPath?.includes(targetId));
    const [open, setOpen] = useState(false);
    const [visible, setVisible] = useState(false);
    const [triangleEnabled, setTriangleEnabled] = useState(false);
    // Set to true when ArrowLeft explicitly hides this submenu. Prevents the
    // open→visible sync effect from automatically re-showing the submenu.
    // Cleared when the submenu is explicitly opened via mouse or keyboard.
    const dismissedByKeyboard = useRef(false);
    useLayoutEffect(() => {
        if (isProgrammaticOpen) {
            dismissedByKeyboard.current = false;
            setOpen(true);
            setVisible(true);
        }
    }, [isProgrammaticOpen]);
    const [closeWithDelay, cancelCloseWithDelay] = useCancellableCallback(() => {
        setOpen(false);
        setTriangleEnabled(false);
        triggerRef.current?.blur();
    }, { delay: SUBMENU_TIMEOUT_DELAY }, [setOpen, setTriangleEnabled]);
    // register with parent ONCE, only when it’s open—this is NOT idempotent!
    useLayoutEffect(() => {
        if (!open)
            return;
        return registerSelfWithParent(() => {
            setOpen(false);
            setVisible(false);
            setTriangleEnabled(false);
        });
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps -- important: only run this with open changes! deps won’t be out-of-date.
    [open]);
    // Sync the open & visible states
    const visibleTimeout = useRef(null);
    useEffect(() => {
        // If we’re opening but it’s not visible, queue up a delay.
        // Skip if the submenu was explicitly dismissed via keyboard (ArrowLeft) — the
        // user closed it and we don’t want to automatically reopen it.
        if (open && !visible && !visibleTimeout.current && !dismissedByKeyboard.current) {
            visibleTimeout.current = window.setTimeout(() => {
                setVisible(true);
                visibleTimeout.current = null;
            }, SUBMENU_OPENED_DELAY_MS);
        }
        else if (!open && !isProgrammaticOpen) {
            // otherwise if we are closing, prevent flickering
            if (visibleTimeout.current) {
                clearTimeout(visibleTimeout.current);
                visibleTimeout.current = null;
            }
            setVisible(false);
            registerOpenSubMenu(undefined); // Close any open submenus of this submenu
        }
        return () => {
            if (visibleTimeout.current) {
                clearTimeout(visibleTimeout.current);
                visibleTimeout.current = null;
            }
        };
    }, [isProgrammaticOpen, open, registerOpenSubMenu, visible]);
    const positioner = useMenuPositioner(POSITION_ARGS);
    const submenuNav = useMenuNav({ popupRef });
    const typeahead = useMenuTypeahead({ popupRef });
    const tabLoop = useTabLoop();
    const { update, props: triangleProps } = useSafeTriangle({
        from: anchorRef,
        to: popupRef,
        enabled: triangleEnabled,
        onTimeout: () => setTriangleEnabled(false),
        portalTarget,
        debug: DEBUG_SAFE_TRIANGLE,
    });
    const handleMouseMove = (e) => {
        if (isClosing.current)
            return;
        if (shouldIgnoreProgrammaticPointerEvent(e.currentTarget))
            return;
        // If the user is known to be using their mouse, then we want to have focus
        // also follow the cursor on enter. This is because the safe triangle
        // deactivates after a short period of no mouse movement. When the triangle
        // disappears, the item behind the cursor that used to be occluded by the
        // triangle needs to get selected.
        if (e.pointerType === 'mouse' && getMovementType() === 'pointer') {
            endProgrammaticOpen();
            cancelCloseWithDelay();
            dismissedByKeyboard.current = false;
            setOpen(true);
            setTriangleEnabled(true);
            update(e);
            e.currentTarget.focus();
        }
    };
    const handleClick = useInteractionHandler((e) => {
        cancelCloseWithDelay();
        dismissedByKeyboard.current = false;
        setOpen(true);
        setVisible(true);
        accept(e);
    }, { eventName: 'click', recordingKey }, [cancelCloseWithDelay, recordingKey]);
    const triggerProps = {
        role: 'menuitem',
        ref,
        tabIndex: open ? 0 : -1,
        onFocus: () => {
            // When the user presses ArrowLeft to close a submenu, focus returns to
            // this trigger. Without this guard, the focus event would immediately
            // reopen the submenu, trapping the user in a close/reopen loop.
            if (LastInteraction.key === 'ArrowLeft')
                return;
            dismissedByKeyboard.current = false;
            registerOpenSubMenu(undefined);
            setOpen(true);
        },
        onPointerDown: handleClick,
        onPointerEnter: handleMouseMove,
        onPointerMove: handleMouseMove,
        onPointerLeave: (e) => {
            releaseProgrammaticPointerItem(e.currentTarget);
            if (e.pointerType === 'mouse' && programmaticTargetId === undefined)
                closeWithDelay();
        },
        onPointerUp: (e) => {
            if (e.pointerType === 'mouse' && isClickHold.current)
                onCloseRoot();
        },
        onKeyDown: (e) => {
            if ([' ', 'ArrowRight', 'Enter'].includes(e.key) && !isAnyModifierPressed(e)) {
                handleClick(e);
                submenuNav.focusManager.focusFirst();
            }
            else if (e.key === 'ArrowLeft' && !isAnyModifierPressed(e) && visible) {
                dismissedByKeyboard.current = true;
                setVisible(false);
                setTriangleEnabled(false);
                accept(e);
            }
        },
        'aria-expanded': open,
        'aria-controls': popupId,
    };
    const triangleRetentionColor = triangleEnabled && interactionMode === 'pointer' ? 'brand' : undefined;
    const openColor = open ? 'secondary' : undefined;
    const { interactionProps, paintProps } = Surface.getSplitProps({
        // Only expose pointer treatments in pointer mode so a stationary pointer and keyboard focus
        // cannot highlight two submenu triggers at once.
        color: triangleRetentionColor ?? openColor,
        hover: interactionMode === 'pointer' && 'brand',
        focusVisible: interactionMode === 'keyboard' && 'brand',
    });
    return (jsxs(Fragment, { children: [jsx("li", { "data-fpl-component": "", ...triggerProps, ...interactionProps, className: item, "data-fpl-checked": hasChecked || undefined, children: jsx(ItemDisplay, { ...paintProps, ref: anchorRef, selectIcon: hasChecked ? jsx(Icon16Dot, {}) : undefined, lead: titleLead, trail: jsxs(Fragment, { children: [titleTrail, jsx("span", { className: submenuTriggerTrail, children: jsx(Icon24ChevronRight, { "aria-hidden": true }) })] }), variant: "default", children: title }) }), jsx(SafeTriangle, { "data-fpl-component": "", onPointerEnter: cancelCloseWithDelay, ...triangleProps() }), open &&
                createPortal(jsx(MenuInternalContext.Provider, { value: menuInternalContext, children: jsx(Popup, { colorTheme: {
                            mode: inheritedTheme.mode,
                            brand: inheritedTheme.brand,
                            enhancedContrast: inheritedTheme.enhancedContrast,
                            ...colorTheme,
                        }, "aria-modal": true, "data-fpl-hidden": visible ? undefined : true, id: popupId, ref: popupRef, anchorRef: anchorRef, open: true, onBlur: (e) => tabLoop.onBlur(e), onKeyDownCapture: typeahead.onKeyDownCapture, onKeyDown: (e) => {
                            if (e.key === 'ArrowLeft' && !isAnyModifierPressed(e)) {
                                dismissedByKeyboard.current = true;
                                setVisible(false);
                                setTriangleEnabled(false);
                                triggerRef.current?.focus();
                                accept(e);
                            }
                            else {
                                tabLoop.onKeyDown(e);
                                submenuNav.popupProps.onKeyDown(e);
                            }
                        }, onPointerEnter: (e) => {
                            if (e.pointerType === 'mouse') {
                                cancelCloseWithDelay();
                                setTriangleEnabled(false);
                            }
                        }, positioner: positioner, children: children }) }), portalTarget)] }));
}
SubMenu.displayName = 'MenuV2.SubMenu';

export { SubMenu };
//# sourceMappingURL=menu-v2.sub-menu.js.map
